import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/Input";
import { useInvoiceableLines, type InvoiceableLine } from "@/api/hooks";
import { seedMissingAllocations } from "./invoiceAllocation";

/**
 * Chọn dòng và số lượng khi lập hóa đơn từ một PI.
 *
 * Khách chốt 14/09/2026: một PI xuất được nhiều hóa đơn, trần đặt ở TỪNG DÒNG.
 * Bảng này điền sẵn phần còn xuất được của mỗi dòng, nên lập trọn một lần vẫn
 * là bấm một cái — người dùng chỉ sửa khi thật sự muốn xuất một phần.
 *
 * Đây là TIỆN LỢI, không phải chốt: máy chủ kiểm lại toàn bộ trong
 * `invoiceAllocation-logic.ts`. Giao diện chặn sớm để người dùng khỏi điền xong
 * cả biểu mẫu rồi mới bị từ chối.
 */
export function InvoiceLineAllocator({
  piId,
  value,
  onChange,
}: {
  piId: string;
  /** piItemId → số lượng xuất lần này. */
  value: Record<string, number>;
  onChange: (next: Record<string, number>) => void;
}) {
  const { t } = useTranslation();
  const query = useInvoiceableLines(piId);
  const lines = query.data?.data;

  /**
   * Điền sẵn MỌI dòng vào giá trị gửi đi, không chỉ dòng người dùng chạm vào.
   *
   * Lỗi tìm ra khi bấm thử 15/09/2026: bảng hiện hai dòng, cả hai điền sẵn số
   * còn lại, người dùng chỉ sửa dòng đầu — và hóa đơn lập ra chỉ có dòng đầu.
   * Dòng thứ hai im lặng rơi mất, trong khi màn hình vừa nói nó sẽ được xuất.
   * Ô nhập hiển thị `value[id] ?? remaining`, nên mắt thấy một đằng còn thứ
   * gửi đi là một nẻo.
   */
  useEffect(() => {
    if (!lines) return;
    const seeded = seedMissingAllocations(lines, value);
    if (seeded) onChange(seeded);
  }, [lines, value, onChange]);

  if (!piId || query.isLoading || !lines) return null;

  const open = lines.filter((l) => l.remaining > 0);
  if (open.length === 0)
    return (
      <p role="alert" className="mt-3 rounded-md bg-warn-tint p-3 text-sm text-warn">
        {t("invoiceWorkspace.nothingLeft")}
      </p>
    );

  const partiallyInvoiced = lines.some((l) => l.invoiced > 0);
  const set = (line: InvoiceableLine, raw: string) => {
    // Ô trống nghĩa là KHÔNG xuất dòng này, không phải NaN.
    const n = raw.trim() === "" ? 0 : Number(raw);
    if (!Number.isInteger(n) || n < 0) return;
    onChange({ ...value, [line.piItemId]: Math.min(n, line.remaining) });
  };

  return (
    <div className="mt-4">
      <p className="text-xs text-ink-faint">{t("invoiceWorkspace.chooseLines")}</p>
      {/*
        Chỉ hiện cột "đã xuất" khi PI này THẬT SỰ đã có hóa đơn trước đó. Với
        278 PI hiện có thì lần lập đầu tiên là phổ biến nhất, và một cột toàn
        số 0 chỉ làm bảng rậm thêm.
      */}
      <div className="mt-2 overflow-x-auto rounded-md border border-line">
        <table className="w-full min-w-[520px] text-sm">
          <thead className="bg-sunken">
            <tr>
              <th className="p-2 text-left">{t("invoiceWorkspace.line")}</th>
              <th className="p-2 text-right">{t("invoiceWorkspace.ordered")}</th>
              {partiallyInvoiced && (
                <th className="p-2 text-right">{t("invoiceWorkspace.alreadyInvoiced")}</th>
              )}
              <th className="p-2 text-right">{t("invoiceWorkspace.remaining")}</th>
              <th className="p-2 text-right">{t("invoiceWorkspace.invoiceNow")}</th>
            </tr>
          </thead>
          <tbody>
            {open.map((l) => (
              <tr key={l.piItemId} className="border-t border-line">
                <td className="p-2">
                  <span className="font-mono">{l.productCode}</span>
                  {l.subRef && <span className="font-mono text-ink-soft"> · {l.subRef}</span>}
                  <p className="text-xs text-ink-soft">{l.description}</p>
                </td>
                <td className="p-2 text-right font-mono tabular">{l.ordered}</td>
                {partiallyInvoiced && (
                  <td className="p-2 text-right font-mono tabular">{l.invoiced}</td>
                )}
                <td className="p-2 text-right font-mono tabular">{l.remaining}</td>
                <td className="p-2 text-right">
                  <Input
                    aria-label={`${t("invoiceWorkspace.invoiceNow")} ${l.productCode}`}
                    className="ml-auto h-9 w-24 text-right font-mono tabular"
                    inputMode="numeric"
                    value={String(value[l.piItemId] ?? l.remaining)}
                    onChange={(e) => set(l, e.target.value)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
